import { JSBI, Pair, Percent } from '@uniswap/sdk'
import { darken } from 'polished'
import React, { useState, useCallback, useEffect } from 'react'
import { ChevronDown, ChevronUp } from 'react-feather'
import { Link } from 'react-router-dom'
import { Text } from 'rebass'
import styled from 'styled-components'
import { useTotalSupply } from '../../data/TotalSupply'

import { useActiveWeb3React } from '../../hooks'
import { useTokenBalance } from '../../state/wallet/hooks'
import { ExternalLink, TYPE } from '../../theme'
import { currencyId } from '../../utils/currencyId'
import { unwrappedToken } from '../../utils/wrappedCurrency'
import { ButtonPrimary, ButtonSecondary, ButtonEmpty } from '../Button'
import { transparentize } from 'polished'
import { CardNoise } from '../earn/styled'

import { useColor } from '../../hooks/useColor'

import Card, { GreyCard, LightCard } from '../Card'
import { AutoColumn } from '../Column'
import CurrencyLogo from '../CurrencyLogo'
import DoubleCurrencyLogo from '../DoubleLogo'
import { RowBetween, RowFixed } from '../Row'
import { Dots } from '../swap/styleds'
import DepositModal from '../PoolModal/DepositModal'
import CreatePoolModal from '../PoolModal/CreatePoolModal'
import WithdrawPoolModal from '../PoolModal/WithdrawPoolModal'

export const FixedHeightRow = styled(RowBetween)`
  height: 24px;
`

export const HoverCard = styled(Card)`
  border: 1px solid transparent;
  :hover {
    border: 1px solid ${({ theme }) => darken(0.06, theme.bg2)};
  }
`
const StyledPositionCard = styled(LightCard) <{ bgColor: any }>`
  border: none;
  background: ${({ theme, bgColor }) =>
        `radial-gradient(91.85% 100% at 1.84% 0%, ${transparentize(0.8, bgColor)} 0%, ${theme.bg3} 100%) `};
  position: relative;
  overflow: hidden;
`

interface PositionCardProps {
    pair: Pair
    showUnwrapped?: boolean
    border?: string
}

export function MinimalPositionCard({ pair, showUnwrapped = false, border }: PositionCardProps) {
    const { account } = useActiveWeb3React()

    const currency0 = showUnwrapped ? pair.token0 : unwrappedToken(pair.token0)
    const currency1 = showUnwrapped ? pair.token1 : unwrappedToken(pair.token1)

    const [showMore, setShowMore] = useState(false)

    const userPoolBalance = useTokenBalance(account ?? undefined, pair.liquidityToken)
    const totalPoolTokens = useTotalSupply(pair.liquidityToken)

    const poolTokenPercentage =
        !!userPoolBalance && !!totalPoolTokens && JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
            ? new Percent(userPoolBalance.raw, totalPoolTokens.raw)
            : undefined

    const [token0Deposited, token1Deposited] =
        !!pair &&
            !!totalPoolTokens &&
            !!userPoolBalance &&
            // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
            JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
            ? [
                pair.getLiquidityValue(pair.token0, totalPoolTokens, userPoolBalance, false),
                pair.getLiquidityValue(pair.token1, totalPoolTokens, userPoolBalance, false)
            ]
            : [undefined, undefined]

    return (
        <>
            {
                userPoolBalance && JSBI.greaterThan(userPoolBalance.raw, JSBI.BigInt(0)) &&
                <GreyCard border={border}>
                    <AutoColumn gap="12px">
                        <FixedHeightRow>
                            <RowFixed>
                                <Text fontWeight={500} fontSize={16}>
                                    Your position
                                </Text>
                            </RowFixed>
                        </FixedHeightRow>
                        <FixedHeightRow onClick={() => setShowMore(!showMore)}>
                            <RowFixed>
                                <DoubleCurrencyLogo currency0={currency0} currency1={currency1} margin={true} size={20} />
                                <Text fontWeight={500} fontSize={20}>
                                    {currency0.symbol}/{currency1.symbol}
                                </Text>
                            </RowFixed>
                            <RowFixed>
                                <Text fontWeight={500} fontSize={20}>
                                    {userPoolBalance ? userPoolBalance.toSignificant(4) : '-'}
                                </Text>
                            </RowFixed>
                        </FixedHeightRow>
                        <AutoColumn gap="4px">
                            <FixedHeightRow>
                                <Text fontSize={16} fontWeight={500}>
                                    Your pool share:
                                </Text>
                                <Text fontSize={16} fontWeight={500}>
                                    {poolTokenPercentage ? poolTokenPercentage.toFixed(6) + '%' : '-'}
                                </Text>
                            </FixedHeightRow>
                            <FixedHeightRow>
                                <Text fontSize={16} fontWeight={500}>
                                    {currency0.symbol}:
                                </Text>
                                {token0Deposited ? (
                                    <RowFixed>
                                        <Text fontSize={16} fontWeight={500} marginLeft={'6px'}>
                                            {token0Deposited?.toSignificant(6)}
                                        </Text>
                                    </RowFixed>
                                ) : (
                                    '-'
                                )}
                            </FixedHeightRow>
                            <FixedHeightRow>
                                <Text fontSize={16} fontWeight={500}>
                                    {currency1.symbol}:
                                </Text>
                                {token1Deposited ? (
                                    <RowFixed>
                                        <Text fontSize={16} fontWeight={500} marginLeft={'6px'}>
                                            {token1Deposited?.toSignificant(6)}
                                        </Text>
                                    </RowFixed>
                                ) : (
                                    '-'
                                )}
                            </FixedHeightRow>
                        </AutoColumn>
                    </AutoColumn>
                </GreyCard>
            }
        </>
    )
}

export default function FullPositionCard({ pair, border }: PositionCardProps) {
    const { account } = useActiveWeb3React()

    const currency0: any = unwrappedToken(pair.token0)
    const currency1: any = unwrappedToken(pair.token1)
    const [showMore, setShowMore] = useState(false)

    const userPoolBalance = useTokenBalance(account ?? undefined, pair.liquidityToken)
    const totalPoolTokens = useTotalSupply(pair.liquidityToken)

    const poolTokenPercentage =
        !!userPoolBalance && !!totalPoolTokens && JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
            ? new Percent(userPoolBalance.raw, totalPoolTokens.raw)
            : undefined

    const [token0Deposited, token1Deposited] =
        !!pair &&
            !!totalPoolTokens &&
            !!userPoolBalance &&
            // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
            JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
            ? [
                pair.getLiquidityValue(pair.token0, totalPoolTokens, userPoolBalance, false),
                pair.getLiquidityValue(pair.token1, totalPoolTokens, userPoolBalance, false)
            ]
            : [undefined, undefined]

    const backgroundColor = useColor(pair?.token0)

    // add / remove modal
    const [modalOpen, setModalOpen] = useState(false)
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const handleDismissWithdraw = useCallback(() => {
        setModalOpen(false)
    }, [setModalOpen])

    const handleDismissCreate = useCallback(() => {
        setCreateModalOpen(false)
    }, [setCreateModalOpen])
    useEffect(()=>{
        console.log('currency0, currency1', currency0, currency1)
    },[])
    return (
        <StyledPositionCard border={border} bgColor={backgroundColor}>
            <CardNoise />
            <AutoColumn gap="12px">
                <FixedHeightRow>
                    <RowFixed>
                        <DoubleCurrencyLogo currency0={currency0} currency1={currency1} margin={true} size={20} />
                        <Text fontWeight={500} fontSize={20}>
                            {!currency0 || !currency1 ? <Dots>Loading</Dots> : `${currency0.symbol}/${currency1.symbol}`}
                        </Text>
                    </RowFixed>

                    <RowFixed gap="8px">
                        <ButtonEmpty
                            padding="6px 8px"
                            borderRadius="12px"
                            width="fit-content"
                            onClick={() => setShowMore(!showMore)}
                        >
                            {showMore ? (
                                <>
                                    {' '}
                                    Manage
                                    <ChevronUp size="20" style={{ marginLeft: '10px' }} />
                                </>
                            ) : (
                                <>
                                    Manage
                                    <ChevronDown size="20" style={{ marginLeft: '10px' }} />
                                </>
                            )}
                        </ButtonEmpty>
                    </RowFixed>
                </FixedHeightRow>

                {showMore && (
                    <AutoColumn gap="8px">
                        <FixedHeightRow>
                            <Text fontSize={16} fontWeight={500}>
                                Your pool tokens:
                            </Text>
                            <Text fontSize={16} fontWeight={500}>
                                {userPoolBalance ? userPoolBalance.toSignificant(4) : '-'}
                            </Text>
                        </FixedHeightRow>
                        <FixedHeightRow>
                            <RowFixed>
                                <Text fontSize={16} fontWeight={500}>
                                    Pooled {currency0.symbol}:
                                </Text>
                            </RowFixed>
                            {token0Deposited ? (
                                <RowFixed>
                                    <Text fontSize={16} fontWeight={500} marginLeft={'6px'}>
                                        {token0Deposited?.toSignificant(6)}
                                    </Text>
                                    <CurrencyLogo size="20px" style={{ marginLeft: '8px' }} currency={currency0} />
                                </RowFixed>
                            ) : (
                                '-'
                            )}
                        </FixedHeightRow>

                        <FixedHeightRow>
                            <RowFixed>
                                <Text fontSize={16} fontWeight={500}>
                                    Pooled {currency1.symbol}:
                                </Text>
                            </RowFixed>
                            {token1Deposited ? (
                                <RowFixed>
                                    <Text fontSize={16} fontWeight={500} marginLeft={'6px'}>
                                        {token1Deposited?.toSignificant(6)}
                                    </Text>
                                    <CurrencyLogo size="20px" style={{ marginLeft: '8px' }} currency={currency1} />
                                </RowFixed>
                            ) : (
                                '-'
                            )}
                        </FixedHeightRow>

                        <FixedHeightRow>
                            <Text fontSize={16} fontWeight={500}>
                                Your pool share:
                            </Text>
                            <Text fontSize={16} fontWeight={500}>
                                {poolTokenPercentage ? poolTokenPercentage.toFixed(2) + '%' : '-'}
                            </Text>
                        </FixedHeightRow>

                        {/* <ButtonSecondary padding="8px" borderRadius="8px">
                            <ExternalLink
                                style={{ width: '100%', textAlign: 'center' }}
                                href={`https://info.quickswap.exchange/account/${account}`}
                            >
                                View accrued fees and analytics<span style={{ fontSize: '11px' }}>↗</span>
                            </ExternalLink>
                        </ButtonSecondary> */}
                        <RowBetween marginTop="10px">
                            <ButtonPrimary
                                padding="8px"
                                borderRadius="8px"
                                as={Link}
                                onClick={() => setCreateModalOpen(true)}
                                to={`#`}
                                width="48%"
                            >
                                Deposit
                            </ButtonPrimary>
                            <ButtonPrimary
                                padding="8px"
                                borderRadius="8px"
                                as={Link}
                                onClick={() => setModalOpen(true)}
                                width="48%"
                                to={`#`}
                            >
                                Withdraw
                            </ButtonPrimary>
                        </RowBetween>
                    </AutoColumn>
                )}
            </AutoColumn>
            {
                currency0 && currency1 &&
                <>
                  
                        <CreatePoolModal
                            isOpen={createModalOpen}
                            onDismiss={handleDismissCreate}
                            isCreate={false}
                            currencyIdA={currency0}
                            currencyIdB={currency1}
                        />
                  
                        <WithdrawPoolModal
                            isOpen={modalOpen}
                            onDismiss={handleDismissWithdraw}
                            currencyIdA={currency0}
                            currencyIdB={currency1}
                        />
                   
                </>
            }

        </StyledPositionCard>
    )
}
